import api from '@/shared/utils/apiConfig';
import type { InventoryActorRef } from '@/modules/inventory/api/inventoryActor.types';
import i18n from '@/shared/i18n';

export const INVENTORY_STATUSES = ['active', 'discontinued'] as const;

export const INVENTORY_STOCK_STATES = ['in_stock', 'low_stock', 'out_of_stock'] as const;

export const INVENTORY_LIST_FILTERS = [
  'all',
  ...INVENTORY_STOCK_STATES,
  'discontinued',
] as const;

export type InventoryCategory = string;
export type InventoryUnit = string;
export type InventoryStatus = (typeof INVENTORY_STATUSES)[number];
export type InventoryStockState = (typeof INVENTORY_STOCK_STATES)[number];
export type InventoryListFilter = (typeof INVENTORY_LIST_FILTERS)[number];

export const INVENTORY_TRACKING_MODES = ['none', 'lot', 'serial', 'serial_in_lot'] as const;
export type InventoryTrackingMode = (typeof INVENTORY_TRACKING_MODES)[number];

export function getInventoryTrackingModeLabel(mode: string): string {
  switch (mode as InventoryTrackingMode) {
    case 'none':
      return i18n.t('inventory:trackingMode.none');
    case 'lot':
      return i18n.t('inventory:trackingMode.lot');
    case 'serial':
      return i18n.t('inventory:trackingMode.serial');
    case 'serial_in_lot':
      return i18n.t('inventory:trackingMode.serialInLot');
    default:
      return mode;
  }
}

export const isSerialTrackingMode = (mode?: string | null): boolean =>
  mode === 'serial' || mode === 'serial_in_lot';

export const isLotFieldTrackingMode = (mode?: string | null): boolean =>
  mode === 'lot' || mode === 'serial_in_lot';

export function getInventoryCategoryLabel(category: string): string {
  switch (category as InventoryCategory) {
    case 'Consumables':
      return i18n.t('inventory:categories.Consumables');
    case 'Instruments':
      return i18n.t('inventory:categories.Instruments');
    case 'Equipment':
      return i18n.t('inventory:categories.Equipment');
    case 'Medicines':
      return i18n.t('inventory:categories.Medicines');
    case 'Implants':
      return i18n.t('inventory:categories.Implants');
    case 'Orthodontic Supplies':
      return i18n.t('inventory:categories.Orthodontic Supplies');
    case 'Office Supplies':
      return i18n.t('inventory:categories.Office Supplies');
    case 'Others':
      return i18n.t('inventory:categories.Others');
    default:
      return category;
  }
}

export function getInventoryUnitLabel(unit: string): string {
  switch (unit as InventoryUnit) {
    case 'Piece':
      return i18n.t('inventory:units.Piece');
    case 'Box':
      return i18n.t('inventory:units.Box');
    case 'Pack':
      return i18n.t('inventory:units.Pack');
    case 'Set':
      return i18n.t('inventory:units.Set');
    case 'Kit':
      return i18n.t('inventory:units.Kit');
    case 'Bottle':
      return i18n.t('inventory:units.Bottle');
    case 'Tube':
      return i18n.t('inventory:units.Tube');
    case 'Syringe':
      return i18n.t('inventory:units.Syringe');
    case 'Gram':
      return i18n.t('inventory:units.Gram');
    case 'Kilogram':
      return i18n.t('inventory:units.Kilogram');
    case 'Milliliter':
      return i18n.t('inventory:units.Milliliter');
    case 'Liter':
      return i18n.t('inventory:units.Liter');
    case 'Abutment':
      return i18n.t('inventory:units.Abutment');
    case 'Other':
      return i18n.t('inventory:units.Other');
    default:
      return unit;
  }
}

export function getInventoryStatusLabel(status: string): string {
  switch (normalizeCatalogStatus(status)) {
    case 'active':
      return i18n.t('inventory:itemStatus.active');
    case 'discontinued':
      return i18n.t('inventory:itemStatus.discontinued');
    default:
      return status;
  }
}

export function getInventoryStockStateLabel(state: string): string {
  switch (state as InventoryStockState) {
    case 'in_stock':
      return i18n.t('inventory:stockState.in_stock');
    case 'low_stock':
      return i18n.t('inventory:stockState.low_stock');
    case 'out_of_stock':
      return i18n.t('inventory:stockState.out_of_stock');
    default:
      return state;
  }
}

export function normalizeCatalogStatus(status: string): InventoryStatus {
  return status === 'discontinued' ? 'discontinued' : 'active';
}

export function computeStockState(
  item: Pick<InventoryItem, 'status' | 'stockState' | 'clinics'>,
  clinicId?: string
): InventoryStockState | undefined {
  if (normalizeCatalogStatus(item.status) === 'discontinued') {
    return undefined;
  }

  if (item.stockState) {
    return item.stockState;
  }

  const scopedClinicId = clinicId && clinicId !== 'all' ? clinicId : undefined;
  const rows = scopedClinicId
    ? (item.clinics ?? []).filter((entry) => getClinicIdFromStock(entry) === scopedClinicId)
    : (item.clinics ?? []);

  if (rows.length === 0) {
    return 'out_of_stock';
  }

  const totalStock = rows.reduce((sum, row) => sum + (row.currentStock ?? 0), 0);
  if (totalStock <= 0) {
    return 'out_of_stock';
  }

  if (rows.some((row) => (row.currentStock ?? 0) <= (row.minStockLevel ?? 0))) {
    return 'low_stock';
  }

  return 'in_stock';
}

export function getInventoryListFilterLabel(filter: string): string {
  if (filter === 'all') {
    return i18n.t('inventory:catalog.allStatuses');
  }
  if (filter === 'discontinued') {
    return getInventoryStatusLabel('discontinued');
  }
  return getInventoryStockStateLabel(filter);
}

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  get manual() { return i18n.t('inventory:movementTypes.manual'); },
  get receipt() { return i18n.t('inventory:movementTypes.receipt'); },
  get receipt_void() { return i18n.t('inventory:movementTypes.receipt_void'); },
  get issue() { return i18n.t('inventory:movementTypes.issue'); },
  get issue_void() { return i18n.t('inventory:movementTypes.issue_void'); },
  get transfer_in() { return i18n.t('inventory:movementTypes.transfer_in'); },
  get transfer_out() { return i18n.t('inventory:movementTypes.transfer_out'); },
  get transfer_void() { return i18n.t('inventory:movementTypes.transfer_void'); },
  get stocktake_adjust() { return i18n.t('inventory:movementTypes.stocktake_adjust'); },
  get stocktake_void() { return i18n.t('inventory:movementTypes.stocktake_void'); },
  get transfer() { return i18n.t('inventory:movementTypes.transfer'); },
};

export interface InventoryClinicRef {
  _id: string;
  name?: string;
  shortName?: string;
}

export interface InventoryClinicStock {
  warehouse?: string | { _id: string; name?: string };
  clinic: string | InventoryClinicRef;
  currentStock: number;
  minStockLevel: number;
}

export interface InventorySupplierRef {
  id: string;
  code?: string;
  name?: string;
}

export interface InventoryItem {
  _id: string;
  itemName: string;
  itemCode: string;
  category: InventoryCategory;
  description?: string;
  unit: InventoryUnit;
  clinics: InventoryClinicStock[];
  manufacturer?: string;
  supplier?: InventorySupplierRef | null;
  costPrice: number;
  sellingPrice?: number;
  lastStockUpdate?: string;
  status: InventoryStatus;
  stockState?: InventoryStockState;
  trackingMode?: InventoryTrackingMode;
  notes?: string;
  isCommissionDeductible?: boolean;
  createdBy?: InventoryActorRef;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryWarehousePolicy {
  warehouse?: string;
  minStockLevel?: number;
}

export interface CreateInventoryPayload {
  itemName: string;
  /** Để trống → backend tự sinh PROD-0001, … */
  itemCode?: string;
  category: InventoryCategory;
  description?: string;
  unit: InventoryUnit;
  costPrice?: number;
  sellingPrice?: number;
  warehousePolicy?: InventoryWarehousePolicy;
  manufacturer?: string;
  /** Supplier master id (FK), or null to clear. */
  supplier?: string | null;
  trackingMode?: InventoryTrackingMode;
  status?: InventoryStatus;
  notes?: string;
  isCommissionDeductible?: boolean;
}

export type UpdateInventoryPayload = Partial<CreateInventoryPayload>;

export type DeleteInventoryResultMode = 'deleted' | 'discontinued';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  count?: number;
  message?: string;
}

export function getWarehouseIdFromStock(stock?: InventoryClinicStock): string | undefined {
  if (!stock?.warehouse) return undefined;
  return typeof stock.warehouse === 'object' ? stock.warehouse._id : stock.warehouse;
}

export function getClinicIdFromStock(stock?: InventoryClinicStock): string | undefined {
  if (!stock?.clinic) return undefined;
  return typeof stock.clinic === 'object' ? stock.clinic._id : stock.clinic;
}

export function getPrimaryClinicStock(
  item: InventoryItem,
  preferredClinicId?: string
): InventoryClinicStock | undefined {
  if (!item.clinics?.length) return undefined;
  if (preferredClinicId && preferredClinicId !== 'all') {
    const match = item.clinics.find(
      (entry) => getClinicIdFromStock(entry) === preferredClinicId
    );
    if (match) return match;
  }
  return item.clinics[0];
}

export function resolveClinicIdForStock(
  item: InventoryItem,
  scopeSelected: string,
  orgClinics: { _id: string }[]
): string | undefined {
  const scoped = scopeSelected !== 'all' ? scopeSelected : undefined;
  const fromItem = getClinicIdFromStock(getPrimaryClinicStock(item, scoped));
  if (fromItem) return fromItem;
  if (scoped) return scoped;
  if (orgClinics.length === 1) return orgClinics[0]._id;
  return undefined;
}

export function buildWarehousePolicyPayload(
  warehouseId: string,
  minStockLevel?: number
): InventoryWarehousePolicy {
  return {
    warehouse: warehouseId,
    ...(minStockLevel != null && !Number.isNaN(minStockLevel)
      ? { minStockLevel }
      : {}),
  };
}

export interface InventoryFilterParams {
  search?: string;
  category?: string;
  status?: string;
  clinicId?: string;
}

export function filterInventoryItems(
  items: InventoryItem[],
  params: InventoryFilterParams
): InventoryItem[] {
  const search = params.search?.trim().toLowerCase();
  const category = params.category && params.category !== 'all' ? params.category : undefined;
  const status = params.status && params.status !== 'all' ? params.status : undefined;

  return items.filter((item) => {
    if (category && item.category !== category) return false;

    if (status) {
      if (status === 'discontinued') {
        if (normalizeCatalogStatus(item.status) !== 'discontinued') return false;
      } else if (status === 'active') {
        if (normalizeCatalogStatus(item.status) !== 'active') return false;
      } else if (['in_stock', 'low_stock', 'out_of_stock'].includes(status)) {
        if (normalizeCatalogStatus(item.status) === 'discontinued') return false;
        const stockState = computeStockState(item, params.clinicId);
        if (stockState !== status) return false;
      }
    }

    if (search) {
      const haystack = [item.itemName, item.itemCode, item.description || '']
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

/** Catalog peek from lists and documents. Opens the item drawer on `/inventory`. */
export function inventoryCatalogItemPath(itemId: string): string {
  return `/inventory?item=${encodeURIComponent(itemId)}`;
}

export async function listInventoryItems(): Promise<InventoryItem[]> {
  const response = await api.get<ApiResponse<InventoryItem[]>>('/inventory');
  return response.data.data || [];
}

export async function getInventoryItem(id: string): Promise<InventoryItem> {
  const response = await api.get<ApiResponse<InventoryItem>>(`/inventory/${id}`);
  if (!response.data.data) {
    throw new Error('Inventory item not found');
  }
  return response.data.data;
}

export async function createInventoryItem(payload: CreateInventoryPayload): Promise<InventoryItem> {
  const response = await api.post<ApiResponse<InventoryItem>>('/inventory', payload);
  if (!response.data.data) {
    throw new Error('Failed to create inventory item');
  }
  return response.data.data;
}

export async function updateInventoryItem(
  id: string,
  payload: UpdateInventoryPayload
): Promise<InventoryItem> {
  const response = await api.put<ApiResponse<InventoryItem>>(`/inventory/${id}`, payload);
  if (!response.data.data) {
    throw new Error('Failed to update inventory item');
  }
  return response.data.data;
}

export async function deleteInventoryItem(
  id: string
): Promise<{ mode: DeleteInventoryResultMode }> {
  const response = await api.delete<ApiResponse<{ mode: DeleteInventoryResultMode }>>(`/inventory/${id}`);
  return response.data.data ?? { mode: 'deleted' };
}

export interface InventoryLotBucket {
  batchNumber: string;
  expiryDate?: string;
  quantity: number;
}

export interface InventorySerialRow {
  serialNumber: string;
  status: string;
  batchNumber?: string;
  expiryDate?: string;
}

export interface LotFefoAllocation {
  batchNumber: string;
  expiryDate?: string;
  quantity: number;
}

export interface SerialTraceResult {
  serialNumber: string;
  status: string;
  batchNumber?: string;
  expiryDate?: string;
  item?: { _id: string; itemCode: string; itemName: string };
  clinic?: { _id: string; name: string };
  patient?: { _id: string; fullName?: string };
  treatmentPlanItem?: string;
  receiptDocumentId?: string;
  issueDocumentId?: string;
}

export async function listInventoryLots(
  itemId: string,
  warehouseId: string
): Promise<InventoryLotBucket[]> {
  const response = await api.get<ApiResponse<InventoryLotBucket[]>>(`/inventory/${itemId}/lots`, {
    params: { warehouseId },
  });
  return response.data.data || [];
}

export async function previewLotFefo(
  itemId: string,
  warehouseId: string,
  quantity: number
): Promise<LotFefoAllocation[]> {
  const response = await api.get<ApiResponse<LotFefoAllocation[]>>(
    `/inventory/${itemId}/lots/fefo-preview`,
    { params: { warehouseId, quantity } }
  );
  return response.data.data || [];
}

export async function listInventorySerials(
  itemId: string,
  warehouseId: string,
  status = 'in_stock',
  options?: { batchNumber?: string; expiryDate?: string }
): Promise<InventorySerialRow[]> {
  const response = await api.get<ApiResponse<InventorySerialRow[]>>(`/inventory/${itemId}/serials`, {
    params: {
      warehouseId,
      status,
      batchNumber: options?.batchNumber,
      expiryDate: options?.expiryDate,
    },
  });
  return response.data.data || [];
}

export async function traceInventorySerial(serialNumber: string): Promise<SerialTraceResult> {
  const response = await api.get<ApiResponse<SerialTraceResult>>(
    `/inventory/serials/${encodeURIComponent(serialNumber)}/trace`
  );
  if (!response.data.data) {
    throw new Error('Serial not found');
  }
  return response.data.data;
}
